import { useEffect, useState } from "react";
import {
    Target,
    Eye,
    Handshake,
    ShieldCheck,
    BadgeCheck,
    Sparkles,
    TrendingUp,
    Users,
} from "lucide-react";
import { PageLayout } from "@/components/PageLayout";
import { Overline } from "@/components/Reveal";
import { MountReveal, CtaLink } from "@/components/pageKit";
import { useSeo } from "@/lib/useSeo";
import { getAboutPage } from "@/lib/sanityQueries";
import { peek } from "@/lib/sanity";

const VALUE_ICONS = [Handshake, ShieldCheck, BadgeCheck, Sparkles, TrendingUp];

const AboutContent = () => {
    const [data, setData] = useState(() => peek(getAboutPage));
    useEffect(() => {
        let active = true;
        getAboutPage()
            .then((res) => {
                if (active && res) setData(res);
            })
            .catch(() => {});
        return () => {
            active = false;
        };
    }, []);

    useSeo(data?.seo);

    const d = data || {};
    const values = d.values?.length ? d.values : [];

    return (
        <>
            {/* HERO */}
            <section
                data-testid="about-hero"
                className="relative overflow-hidden pt-36 pb-16 sm:pt-44 sm:pb-20"
            >
                <div className="pointer-events-none absolute inset-0 el-grid-bg" aria-hidden />
                <div className="relative mx-auto max-w-4xl px-5 text-center sm:px-8">
                    <MountReveal>
                        <span className="inline-flex items-center">
                            <Overline>About EasiLinked</Overline>
                        </span>
                    </MountReveal>
                    <MountReveal delay={0.05}>
                        <h1
                            data-testid="about-hero-heading"
                            className="mt-7 font-heading text-4xl font-extrabold leading-[1.02] tracking-tight text-brand-text sm:text-6xl"
                        >
                            {d.heroHeadline || "Technology Leadership Built Around Your Business."}
                        </h1>
                    </MountReveal>
                    <MountReveal delay={0.1}>
                        <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-brand-muted sm:text-lg">
                            {d.heroSupportingMessage ||
                                "EasiLinked helps growing organizations become secure, structured, and scalable."}
                        </p>
                    </MountReveal>
                </div>
            </section>

            {/* COMPANY STORY */}
            <section
                data-testid="about-story"
                className="mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-20"
            >
                <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-start">
                    <div className="lg:col-span-5">
                        <MountReveal>
                            <Overline>Why We Exist</Overline>
                        </MountReveal>
                        <MountReveal delay={0.05}>
                            <h2 className="mt-5 font-heading text-3xl font-extrabold leading-[1.05] tracking-tight text-brand-text sm:text-4xl">
                                {d.companyStoryHeadline ||
                                    "Enterprise Standards Shouldn't Be Reserved for Enterprise Companies."}
                            </h2>
                        </MountReveal>
                    </div>
                    <div className="lg:col-span-7">
                        <MountReveal delay={0.1}>
                            <p className="text-base leading-relaxed text-brand-muted sm:text-lg">
                                {d.companyStory}
                            </p>
                        </MountReveal>
                    </div>
                </div>
            </section>

            {/* MISSION + VISION */}
            <section
                data-testid="about-mission-vision"
                className="border-y border-brand-line bg-brand-surface py-20 sm:py-24"
            >
                <div className="mx-auto grid max-w-7xl grid-cols-1 gap-5 px-5 sm:px-8 lg:grid-cols-2">
                    <MountReveal>
                        <div className="h-full rounded-3xl border border-brand-line bg-brand-bg p-8 sm:p-10">
                            <span className="grid h-12 w-12 place-items-center rounded-xl border border-brand-line text-brand-accent">
                                <Target className="h-5 w-5" />
                            </span>
                            <Overline className="mt-6 flex w-fit">Our Mission</Overline>
                            <p className="mt-4 text-lg leading-relaxed text-brand-text">{d.mission}</p>
                        </div>
                    </MountReveal>
                    <MountReveal delay={0.06}>
                        <div className="h-full rounded-3xl border border-brand-line bg-brand-bg p-8 sm:p-10">
                            <span className="grid h-12 w-12 place-items-center rounded-xl border border-brand-line text-brand-accent">
                                <Eye className="h-5 w-5" />
                            </span>
                            <Overline className="mt-6 flex w-fit">Our Vision</Overline>
                            <p className="mt-4 text-lg leading-relaxed text-brand-text">{d.vision}</p>
                        </div>
                    </MountReveal>
                </div>
            </section>

            {/* VALUES */}
            <section
                data-testid="about-values"
                className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-24"
            >
                <div className="max-w-2xl">
                    <MountReveal>
                        <Overline>{d.valuesHeadline || "How We Work"}</Overline>
                    </MountReveal>
                    <MountReveal delay={0.05}>
                        <h2 className="mt-5 font-heading text-3xl font-extrabold leading-[1.05] tracking-tight text-brand-text sm:text-4xl">
                            The values behind every engagement
                        </h2>
                    </MountReveal>
                </div>
                <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                    {values.map((v, i) => {
                        const Icon = VALUE_ICONS[i] || VALUE_ICONS[VALUE_ICONS.length - 1];
                        return (
                            <MountReveal key={v._key || i} delay={(i % 3) * 0.06}>
                                <div
                                    data-testid={`about-value-${i}`}
                                    className="h-full rounded-2xl border border-brand-line bg-brand-surface p-6"
                                >
                                    <span className="grid h-12 w-12 place-items-center rounded-xl border border-brand-line bg-brand-bg text-brand-accent">
                                        <Icon className="h-5 w-5" />
                                    </span>
                                    <h3 className="mt-5 font-heading text-lg font-bold tracking-tight text-brand-text">
                                        {v.title}
                                    </h3>
                                    <p className="mt-2 text-sm leading-relaxed text-brand-muted">
                                        {v.description}
                                    </p>
                                </div>
                            </MountReveal>
                        );
                    })}
                </div>
            </section>

            {/* LEADERSHIP PHILOSOPHY */}
            <section
                data-testid="about-leadership"
                className="border-y border-brand-line bg-brand-surface py-20 sm:py-24"
            >
                <div className="mx-auto max-w-3xl px-5 text-center sm:px-8">
                    <MountReveal>
                        <span className="grid h-12 w-12 mx-auto place-items-center rounded-xl border border-brand-line bg-brand-bg text-brand-accent">
                            <Users className="h-5 w-5" />
                        </span>
                    </MountReveal>
                    <MountReveal delay={0.05}>
                        <h2 className="mt-6 font-heading text-3xl font-extrabold leading-[1.05] tracking-tight text-brand-text sm:text-4xl">
                            {d.leadershipHeadline || "Business-First Technology Leadership"}
                        </h2>
                    </MountReveal>
                    <MountReveal delay={0.1}>
                        <p className="mt-6 text-base leading-relaxed text-brand-muted sm:text-lg">
                            {d.leadershipMessage}
                        </p>
                    </MountReveal>
                </div>
            </section>

            {/* FINAL CTA */}
            <section
                data-testid="about-closing-cta"
                className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-28"
            >
                <div className="relative overflow-hidden rounded-card border border-brand-line bg-brand-surface px-6 py-20 text-center sm:px-16 sm:py-24">
                    <div className="pointer-events-none absolute inset-0 el-grid-bg opacity-40" aria-hidden />
                    <MountReveal>
                        <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-brand-accent">
                            Start the conversation
                        </span>
                    </MountReveal>
                    <MountReveal delay={0.05}>
                        <h2 className="mx-auto mt-6 max-w-3xl font-heading text-4xl font-extrabold leading-[1.0] tracking-tight text-brand-text sm:text-5xl">
                            {d.finalCtaHeadline || "Let's Connect You to What's Next."}
                        </h2>
                    </MountReveal>
                    <MountReveal delay={0.1}>
                        <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-brand-muted">
                            {d.finalCtaMessage}
                        </p>
                    </MountReveal>
                    <MountReveal delay={0.15}>
                        <div className="mt-10 flex justify-center">
                            <CtaLink
                                href={d.finalCta?.url || "#"}
                                label={d.finalCta?.label || "Schedule a Conversation"}
                                openInNewTab={d.finalCta?.openInNewTab}
                                variant="primary"
                                testid="about-closing-cta-button"
                            />
                        </div>
                    </MountReveal>
                </div>
            </section>
        </>
    );
};

export default function AboutPage() {
    return (
        <PageLayout>
            <AboutContent />
        </PageLayout>
    );
}
